import { useCallback, useEffect, useRef, useState, type ComponentType, type CSSProperties, type ReactNode } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Circle,
  Download,
  Eraser,
  Hash,
  Loader2,
  Minus,
  Monitor,
  MoveDownRight,
  Pencil,
  Plus,
  RectangleHorizontal,
  Route,
  Ruler,
  Scan,
  StretchHorizontal,
} from 'lucide-react';
import { PolySmoothIcon, PolyStraightIcon } from './polygonIcons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { AttachedAction } from './AttachedAction';
import { RotateControl } from './RotateControl';
import { LayoutControl, type CompareLayout } from './LayoutControl';
import { ClampedText } from '@/components/ClampedText';
import { type DicomJson } from '../../data/dicomTags';
import { deriveSeriesInfo } from '../../hooks/useStudyThumbnail';
import type { DocumentControls } from '../document/DocumentView';

/** The four shape tools grouped under the shapes dropdown. */
export type ShapeKind = 'rectangle' | 'ellipse' | 'polygon-straight' | 'polygon-smooth';

const SHAPES: { kind: ShapeKind; label: string; Icon: ComponentType<{ className?: string }> }[] = [
  // A wide rectangle, not a square: the square belongs to the arrangement
  // control in the same bar, and what you draw is rarely square anyway.
  { kind: 'rectangle', label: 'Rectangle (R)', Icon: RectangleHorizontal },
  { kind: 'ellipse', label: 'Ellipse (E)', Icon: Circle },
  { kind: 'polygon-straight', label: 'Polygon (P)', Icon: PolyStraightIcon },
  { kind: 'polygon-smooth', label: 'Smooth polygon (S)', Icon: PolySmoothIcon },
];
const SHAPE_BY_KIND = Object.fromEntries(SHAPES.map((s) => [s.kind, s])) as Record<
  ShapeKind,
  (typeof SHAPES)[number]
>;

/** The annotation tools as the single header and the compare bar both draw them. */
export interface AnnotationToolProps {
  /** Whether the measure tool is armed. */
  measureActive?: boolean;
  /** Toggle the measure (distance measurement) tool. */
  onToggleMeasure?: () => void;
  /** Whether the arrow tool is armed. */
  arrowActive?: boolean;
  /** Toggle the arrow tool. */
  onToggleArrow?: () => void;
  /** The shape shown on the shapes-group button (armed shape, else last used). */
  shapeSelected?: ShapeKind;
  /** Which shape tool is currently armed (null if none). */
  shapeActive?: ShapeKind | null;
  /** Toggle the shapes group (arm/disarm the selected shape). */
  onToggleShape?: () => void;
  /** Pick a shape from the dropdown (and arm it). */
  onPickShape?: (kind: ShapeKind) => void;
  /** Whether the counter (point-counting) tool is armed. */
  counterActive?: boolean;
  /** Toggle the counter tool. */
  onToggleCounter?: () => void;
  /** Finish the count (it is saved and named): offered beside the counter while it is armed. */
  onDoneCounting?: () => void;
  /** Remove every annotation the person drew on the current slide. */
  onClearAnnotations?: () => void;
  /** Number of the person's own annotations on the current slide (gates the clear button). */
  annotationCount?: number;
  /**
   * The tools are shown but switched off, with this as the reason: somebody
   * who may view the slide and not annotate it. Seeing the tools greyed out
   * says the slide takes annotations, just not from them.
   */
  toolsDisabledReason?: string;
}

/** Whether AnnotationTools draws anything: tools to arm, or the greyed-out set. */
export function hasAnnotationTools(t: AnnotationToolProps): boolean {
  return (
    (!!t.toolsDisabledReason && !t.onToggleMeasure) ||
    !!t.onToggleMeasure ||
    !!t.onToggleArrow ||
    !!(t.onPickShape && t.shapeSelected) ||
    !!t.onToggleCounter ||
    !!(t.onClearAnnotations && (t.annotationCount ?? 0) > 0)
  );
}

/** The annotation tools — measure, arrow, the shapes, the counter, clear — or, switched off, the greyed-out set with the reason. */
export function AnnotationTools({
  measureActive,
  onToggleMeasure,
  arrowActive,
  onToggleArrow,
  shapeSelected,
  shapeActive,
  onToggleShape,
  onPickShape,
  counterActive,
  onToggleCounter,
  onDoneCounting,
  onClearAnnotations,
  annotationCount = 0,
  toolsDisabledReason,
}: AnnotationToolProps) {
  if (toolsDisabledReason && !onToggleMeasure) {
    return (
      <span className="flex items-center gap-0.5" title={toolsDisabledReason}>
        {[Ruler, MoveDownRight, SHAPE_BY_KIND[shapeSelected ?? 'rectangle'].Icon, Hash].map((Icon, i) => (
          <button
            key={i}
            type="button"
            disabled
            // On the button as well as the row: some browsers show no
            // tooltip at all over a disabled control's ancestors.
            title={toolsDisabledReason}
            aria-label={toolsDisabledReason}
            className="inline-flex h-8 w-8 cursor-not-allowed items-center justify-center rounded-md text-muted-foreground opacity-40"
          >
            <Icon className="h-4 w-4" />
          </button>
        ))}
      </span>
    );
  }
  return (
    <>
      {onToggleMeasure && (
        <IconBtn label="Measure distance (M)" onClick={onToggleMeasure} active={measureActive}>
          <Ruler className="h-4 w-4" />
        </IconBtn>
      )}
      {onToggleArrow && (
        <IconBtn label="Arrow (A)" onClick={onToggleArrow} active={arrowActive}>
          <MoveDownRight className="h-4 w-4" />
        </IconBtn>
      )}
      {onPickShape && shapeSelected && (
        <ShapeToolButton
          selected={shapeSelected}
          active={shapeActive ?? null}
          onToggle={onToggleShape ?? (() => {})}
          onPick={onPickShape}
        />
      )}
      {onToggleCounter && (
        // Counting has no natural end, so the way out sits on the tool.
        <AttachedAction
          action={
            counterActive && onDoneCounting
              ? { label: 'Done', hint: 'Finish counting (Esc)', onClick: onDoneCounting }
              : null
          }
        >
          <IconBtn label="Count points (C)" onClick={onToggleCounter} active={counterActive}>
            <Hash className="h-4 w-4" />
          </IconBtn>
        </AttachedAction>
      )}
      {onClearAnnotations && annotationCount > 0 && (
        <IconBtn label="Clear your annotations" onClick={onClearAnnotations}>
          <Eraser className="h-4 w-4" />
        </IconBtn>
      )}
    </>
  );
}

interface Props extends AnnotationToolProps {
  /** Selected series metadata (any instance carries the study tags). */
  metadata: DicomJson[] | undefined;
  /** Human-friendly name of the current slide/series. */
  slideLabel: string;
  /** The raw SeriesInstanceUID — surfaced only on hover / click-to-copy. */
  seriesUID: string | undefined;
  /** Enter compare mode in the chosen arrangement (study has ≥2 series). */
  onCompare?: (layout: CompareLayout) => void;
  /** Whether the study has enough series for the grid: three or more. */
  canGrid?: boolean;
  /** Current view rotation in degrees (0–360). */
  rotationDeg?: number;
  /** Set the absolute view rotation in degrees (engine supports rotation). */
  onRotate?: (deg: number) => void;
  /** Toggle fullscreen on the viewport. */
  onFullscreen?: () => void;
  /** Open the study's details editor: given only to somebody who may write the study. */
  onEditDetails?: () => void;
  /** Open or close the review scan (a whole-slide engine in single mode). */
  onToggleReviewScan?: () => void;
  reviewScanActive?: boolean;
  /** The scan's code is on its way after a press. */
  reviewScanLoading?: boolean;
  /** Fetch the scan's code ahead of the press. */
  onPrefetchReviewScan?: () => void;
  /** An open document's reader, where a slide's tools are: its pages and fit, its zoom, its download. */
  document?: DocumentControls | null;
  /** Arm or put down the snapshot (a slide whose engine hands out its tiles, single mode): with the tools, after the counter. */
  onToggleCapture?: () => void;
  /** The snapshot is armed: the next drag on the slide chooses what to export. */
  captureActive?: boolean;
  /** An X-ray's window/level control (WindowControl): after the tools, behind a divider. */
  windowControl?: ReactNode;
}

/** Room (px) kept between the title and the tools centred beside it. */
const TITLE_ROOM = 24;

/** Context bar above the viewport: the current slide on the left (friendly name;
 *  UID behind hover + click-to-copy) and view actions on the right. Patient
 *  identity lives in the global top bar (see AppLayout). */
export function StudyHeader({
  metadata,
  slideLabel,
  seriesUID,
  onCompare,
  canGrid,
  rotationDeg = 0,
  onRotate,
  onFullscreen,
  onEditDetails,
  onToggleReviewScan,
  reviewScanActive,
  reviewScanLoading,
  onPrefetchReviewScan,
  document: doc,
  onToggleCapture,
  captureActive,
  windowControl,
  ...tools
}: Props) {
  // The image as the navigator names it ("5 of 7 · IHC ER · ER · Breast,
  // Left"); a slide's container id where there is no name yet. Beside it, the
  // first of the description and the coded anatomic site the title does not
  // already say — the name IS the description, so it would otherwise read
  // "IHC ER" twice.
  const info = deriveSeriesInfo(metadata ?? []);
  const title = slideLabel || info.slideId || '';
  const subtitle = firstNotInTitle(title, [info.description, info.anatomicSite]);

  // The snapshot sits with the tools in the centre, after the counter: for
  // anyone who can see the slide, so beside greyed-out tools too, and alone
  // on an image that has no tools.
  const anyTool = hasAnnotationTools(tools);
  // The tools are centred in the bar whatever is beside them, so the title on
  // the left is cut short of them, with room to spare (user, 2026-10-16): a
  // long name ran on under the tools. Measured, as the tools change with the
  // image; and whichever bar is in the centre (an image's tools, a document's
  // reader), the one there now: a ref callback follows the element itself.
  const [centreWidth, setCentreWidth] = useState(0);
  const centreObserver = useRef<ResizeObserver | null>(null);
  const centreRef = useCallback((el: HTMLDivElement | null) => {
    centreObserver.current?.disconnect();
    centreObserver.current = null;
    setCentreWidth(el?.offsetWidth ?? 0);
    if (!el || typeof ResizeObserver === 'undefined') return;
    centreObserver.current = new ResizeObserver(() => setCentreWidth(el.offsetWidth));
    centreObserver.current.observe(el);
  }, []);
  useEffect(() => () => centreObserver.current?.disconnect(), []);
  const captureButton = onToggleCapture ? (
    <IconBtn label="Download snapshot" onClick={onToggleCapture} active={captureActive}>
      <Download className="h-4 w-4" />
    </IconBtn>
  ) : null;

  return (
    <div className="relative z-20 flex h-12 shrink-0 items-center gap-x-3 border-b px-3 max-md:h-auto max-md:min-h-12 max-md:flex-wrap max-md:gap-y-1 max-md:py-1.5">
      <div
        className={cn('flex min-w-0 items-center gap-x-3', centreWidth > 0 && 'md:max-w-[calc(50%-var(--title-room))]')}
        style={{ '--title-room': `${centreWidth / 2 + TITLE_ROOM}px` } as CSSProperties}
      >
        {/* Cut with "…" at the room it has; the description goes first. */}
        {title && <SlideTitle label={title} uid={seriesUID} className="shrink" />}
        {subtitle && (
          <ClampedText text={subtitle} className="min-w-0 shrink-[999] truncate text-sm text-muted-foreground" />
        )}
      </div>
      {/* Annotation tools, centered in the bar. */}
      {(anyTool || captureButton || windowControl) && (
        <div
          ref={centreRef}
          className="absolute left-1/2 flex -translate-x-1/2 items-center gap-0.5 max-md:static max-md:order-last max-md:w-full max-md:translate-x-0"
        >
          <AnnotationTools {...tools} />
          {anyTool && captureButton && <span className="mx-1 h-5 w-px bg-border" aria-hidden />}
          {captureButton}
          {(anyTool || captureButton) && windowControl && <span className="mx-1 h-5 w-px bg-border" aria-hidden />}
          {windowControl}
        </div>
      )}
      {/* A document's reader, where a slide's tools are: its pages and fit,
          its zoom, its download. A document has no compare. */}
      {doc && (
        <div
          ref={centreRef}
          className="absolute left-1/2 flex -translate-x-1/2 items-center gap-0.5 max-md:static max-md:order-last max-md:w-full max-md:translate-x-0"
        >
          <IconBtn label="Previous page" onClick={() => doc.goto(doc.page - 1)} disabled={doc.page <= 1}>
            <ChevronLeft className="h-4 w-4" />
          </IconBtn>
          <PageField page={doc.page} pages={doc.pages} onGo={doc.goto} />
          <IconBtn label="Next page" onClick={() => doc.goto(doc.page + 1)} disabled={doc.page >= doc.pages}>
            <ChevronRight className="h-4 w-4" />
          </IconBtn>
          {/* The whole page, and from there back to the width: the two ends of the zoom. */}
          <IconBtn
            label={doc.fit === 'page' ? 'Fit the width (0)' : 'Fit the whole page (1)'}
            onClick={() => doc.zoom(doc.fit === 'page' ? 'width' : 'page')}
          >
            {doc.fit === 'page' ? <StretchHorizontal className="h-4 w-4" /> : <Scan className="h-4 w-4" />}
          </IconBtn>
          <span className="mx-1 h-5 w-px bg-border" aria-hidden />
          {/* A slide's zoom keys, from the whole page on past the width; the
              readout is of the width: 100% fills it, less shows more of the
              page, more goes closer. */}
          <IconBtn label="Zoom out (−)" onClick={() => doc.zoom('out')} disabled={doc.fit === 'page'}>
            <Minus className="h-4 w-4" />
          </IconBtn>
          <span role="status" aria-label="Zoom" className="min-w-[3rem] px-1 text-center text-sm tabular-nums text-muted-foreground">
            {doc.percent}%
          </span>
          <IconBtn label="Zoom in (+)" onClick={() => doc.zoom('in')} disabled={doc.percent >= doc.max}>
            <Plus className="h-4 w-4" />
          </IconBtn>
          <span className="mx-1 h-5 w-px bg-border" aria-hidden />
          <IconBtn label="Download PDF" onClick={doc.download}>
            <Download className="h-4 w-4" />
          </IconBtn>
        </div>
      )}
      <div className="ml-auto flex shrink-0 items-center gap-0.5">
        {onEditDetails && (
          <IconBtn label="Edit details" onClick={onEditDetails}>
            <Pencil className="h-4 w-4" />
          </IconBtn>
        )}
        {onToggleReviewScan && (
          <IconBtn
            label="Guided review"
            onClick={onToggleReviewScan}
            active={reviewScanActive}
            onHover={onPrefetchReviewScan}
            // Space plays the scan: left focused here, the next Space would
            // press this button again and close the bar instead.
            releaseFocus
          >
            {/* Turned a quarter clockwise (the user, 2026-10-01): the path runs in columns. */}
            {reviewScanLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Route className="h-4 w-4 rotate-90" />}
          </IconBtn>
        )}
        {/* Same control as the compare toolbar, in the same place. The square
            says what you are looking at — one slide; picking a split from the
            dropdown is what opens compare. */}
        {onCompare && (
          <LayoutControl
            value="single"
            offer={(v) => v !== 'grid' || !!canGrid}
            onSelect={(v) => v !== 'single' && onCompare(v)}
            label="Compare mode (D)"
          />
        )}
        {onRotate && <RotateControl deg={rotationDeg} onRotate={onRotate} />}
        {onFullscreen && (
          <IconBtn label="Fullscreen (F)" onClick={onFullscreen}>
            <Monitor className="h-4 w-4" />
          </IconBtn>
        )}
      </div>
    </div>
  );
}

/**
 * The first candidate the title does not already carry — as the whole of it,
 * as a part between its separators ("5 of 7 · IHC ER · Breast, Left" carries
 * "IHC ER", and a name with " · " in it is still one part), or as the site
 * before its side (it carries "Breast"). Shared with the compare panes, which
 * name a slide the same way.
 */
export function firstNotInTitle(title: string, candidates: (string | undefined)[]): string | undefined {
  const padded = ` · ${title} · `;
  return candidates.find((c) => !!c && !padded.includes(` · ${c} · `) && !padded.includes(` · ${c}, `));
}

/**
 * The page a document is on, as a number to type over: Enter goes there,
 * leaving the field puts back the page the reader is on.
 */
function PageField({ page, pages, onGo }: { page: number; pages: number; onGo: (page: number) => void }) {
  const [text, setText] = useState(String(page));
  useEffect(() => setText(String(page)), [page]);
  return (
    <form
      className="flex items-center gap-1 px-1 text-sm tabular-nums text-muted-foreground"
      onSubmit={(e) => {
        e.preventDefault();
        const n = Number(text);
        if (Number.isInteger(n)) onGo(n);
        else setText(String(page));
      }}
    >
      <input
        aria-label="Page"
        className="h-7 w-10 rounded-md border bg-transparent text-center text-sm text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
        value={text}
        inputMode="numeric"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => setText(String(page))}
      />
      <span>/ {pages}</span>
    </form>
  );
}

function IconBtn({
  label,
  onClick,
  active,
  disabled,
  onHover,
  releaseFocus,
  children,
}: {
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  /** The pointer or focus arrived: a chance to fetch ahead of the click. */
  onHover?: () => void;
  /** Give focus back after a mouse click (keyboard users keep it). */
  releaseFocus?: boolean;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          disabled={disabled}
          onPointerEnter={onHover}
          onFocus={onHover}
          onMouseUp={releaseFocus ? (e) => e.currentTarget.blur() : undefined}
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
            active
              ? 'bg-accent text-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            'disabled:pointer-events-none disabled:opacity-40',
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

// Shapes group: one toolbar button showing the selected shape, with an attached
// vertical dropdown of all four shapes. The dropdown sits flush under the button
// (no gap, so it reads as an extension) and each row highlights on hover, like the
// horizontal toolbar but vertical.
function ShapeToolButton({
  selected,
  active,
  onToggle,
  onPick,
}: {
  selected: ShapeKind;
  active: ShapeKind | null;
  onToggle: () => void;
  onPick: (kind: ShapeKind) => void;
}) {
  const SelectedIcon = SHAPE_BY_KIND[selected].Icon;
  const tint = (on: boolean) =>
    on ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground';
  return (
    <div className="group relative">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onToggle}
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
              // On hover the button becomes the top of the dropdown box: same popover
              // background and border, bottom edge open so it merges with the panel.
              'group-hover:rounded-b-none group-hover:border group-hover:border-b-0 group-hover:shadow-md',
              active
                ? 'bg-accent text-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground group-hover:bg-popover group-hover:text-foreground',
            )}
          >
            <SelectedIcon className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        {/* Right side so it clears the dropdown that opens below. */}
        <TooltipContent side="top">{SHAPE_BY_KIND[selected].label}</TooltipContent>
      </Tooltip>
      {/* Attached vertical dropdown — flush to the button's bottom edge. */}
      <div className="absolute left-0 top-full z-30 hidden w-8 flex-col overflow-hidden rounded-b-md border border-t-0 bg-popover shadow-md group-hover:flex">
        {SHAPES.map((s) => (
          <Tooltip key={s.kind}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onPick(s.kind)}
                className={cn('inline-flex h-8 w-full items-center justify-center transition-colors', tint(s.kind === active))}
              >
                <s.Icon className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            {/* Right of each row so the label never covers the icons below it. */}
            <TooltipContent side="right">{s.label}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}

// Rotation group: a toolbar button (click = snap to the next 90° right) with an
// attached hover dropdown for fine control — a vertical slider (up = CCW, down =
// CW) over a numeric input. Both drive the same absolute rotation; angle wraps
// in [0, 360). Mirrors the shapes dropdown's hover/popover styling.
/** The slide's identifier, with its full Series UID behind a tooltip and
 *  click-to-copy. Shared with the compare panes so a slide is named the same way
 *  wherever it appears. */
export function SlideTitle({
  label,
  uid,
  className,
}: {
  label: string;
  uid: string | undefined;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    if (!uid) return;
    navigator.clipboard?.writeText(uid).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      },
      () => {},
    );
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={copy}
          className={cn('min-w-0 shrink-0 truncate text-sm font-semibold', className)}
        >
          {label}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs">
        <div className="break-all font-mono text-xs">{uid ?? '—'}</div>
        <div className="mt-1 text-[10px] text-muted-foreground">
          {copied ? 'Copied' : 'Series UID · click to copy'}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
